import { z } from "zod";

/// VIEW-4's admin-only ledger view (`GET .../leave/ledger`, `LEAVE_VIEW_WORKSPACE`).
/// Rows carry adjustment reasons and actor ids, which is more than any other
/// leave surface promises the person the allocation belongs to: this is why
/// it is admin only rather than the redacted self-view the employee gets
/// (that route ships in a later task).

export const LEAVE_LEDGER_ENTRY_KINDS = [
  "allocation",
  "carry_forward",
  "accrual",
  "consumption",
  "compensatory_grant",
  "adjustment",
  "expiry",
  "reversal",
] as const;
export const leaveLedgerEntryKindSchema = z.enum(LEAVE_LEDGER_ENTRY_KINDS);
export type LeaveLedgerEntryKind = z.infer<typeof leaveLedgerEntryKindSchema>;

export const LEDGER_DOCUMENT_TYPES = [
  "leave_application",
  "compensatory_request",
  "ledger_entry",
  "allocation",
] as const;
export const ledgerDocumentTypeSchema = z.enum(LEDGER_DOCUMENT_TYPES);
export type LedgerDocumentType = z.infer<typeof ledgerDocumentTypeSchema>;

export const ledgerDocumentSchema = z
  .object({ type: ledgerDocumentTypeSchema, id: z.string() })
  .strict();

/// One ledger row with the running total after it, in EFFECTIVE-date order
/// (the date the row takes effect, not the date it was written).
///
/// `effectiveAfterAsOf` marks rows the balance card does not count yet: the
/// card sums `fromDate <= asOf`, while this view keeps every row including
/// leave already approved for later. `runningBalance` accumulates through
/// EVERY row, future-effective ones included, which is why the view carries
/// two separate closing figures below rather than one.
export const ledgerRowSchema = z
  .object({
    id: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    effectiveAfterAsOf: z.boolean(),
    kind: leaveLedgerEntryKindSchema,
    /// Signed. Positive credits, negative consumes.
    days: z.string(),
    runningBalance: z.string(),
    isCarryForward: z.boolean(),
    isLwp: z.boolean(),
    reason: z.string().nullable(),
    actorUserId: z.string().nullable(),
    reversesEntryId: z.string().nullable(),
    document: ledgerDocumentSchema.nullable(),
    createdAt: z.string(),
  })
  .strict();
export type LedgerRow = z.infer<typeof ledgerRowSchema>;

/// GET .../leave/ledger?membershipId=&leaveTypeId=&allocationId=&asOf=
///
/// TWO closing figures, deliberately not one:
///
/// - `balanceAsOf` sums rows effective on or before `asOf`. This equals the
///   balance card's `available`, to the digit. When a dispute is "why is
///   this 7.5", this is the 7.5.
/// - `projectedBalance` sums every row, including leave already approved for
///   later, and is what `rows[rows.length - 1].runningBalance` actually
///   equals (the running total accumulates through future-effective rows
///   too). Treating "the last row's runningBalance" as the closing figure
///   silently renders `projectedBalance` where `balanceAsOf` was wanted; a
///   fixture made only of historical rows cannot catch this, because the two
///   only diverge once a future-effective row exists.
export const ledgerViewSchema = z
  .object({
    membershipId: z.string(),
    /// Who this is, so a deep link or a refresh does not render a bare id.
    displayName: z.string(),
    leaveTypeId: z.string(),
    leaveTypeName: z.string(),
    allocationId: z.string(),
    allocationFromDate: z.string(),
    allocationToDate: z.string(),
    /// The date the two figures below are split on. Echoed back, never
    /// assumed to be "today".
    asOf: z.string(),
    balanceAsOf: z.string(),
    projectedBalance: z.string(),
    rows: z.array(ledgerRowSchema),
  })
  .strict();
export type LedgerView = z.infer<typeof ledgerViewSchema>;
